/**
 * The station screen's Charter lines (M5 spec §15.5; plan C18): the **state banner** ("STRIKE: production halted",
 * "DISABLED by Varro Security: services unavailable") and the **owner line** with the system's holder and tariff
 * ("Tethys claim · tariff 6%"). A corporation claim reads "‹Corp› claim · tariff 4%" or "‹Corp› claim · tariff not set"
 * (D-424 D5, spec §27.7) from the system's `ClaimView.corp`, which the source passes with the station.
 *
 * The worker words the banner (`StationView.banner`: a disabled station first, else the system's weightiest active
 * or recovering state). M2's station screen renders `<StateBanner stationId=… />` in its header; the Charter HUD
 * feature binds the source (`setStateBannerSource`) to the strategic client's cached system view, so the screen
 * needs no strategic wiring of its own. Unbound (the sandbox, tests), it renders nothing. UI events only.
 */
import type { ReactNode } from 'react'
import { factionShort } from '../../charter/tolls'
import { FACTION_OUTLAW } from '../../charter/hostilityWriter'
import { CORP_CLAIM_LABELS, UNCLAIMED_LABELS, corpClaimName, corpTariffText, fillLabel } from '../../features/charterHud/hudModel'
import { FACTION, PLAYER_FACTION_ID, type ClaimCorpView, type StationView } from '../../strategic/types'

export type BannerTone = 'alert' | 'warn' | 'good'

export interface StateBannerModel {
  /** The banner line, or null when the station and its system are calm. */
  text: string | null
  tone: BannerTone
  /** "Tethys claim · tariff 6%", "Clan Vex hold · lawless", "UNCLAIMED · NO CLAIM LAW" (CL-7). */
  owner: string
  /** The plain line under an unclaimed owner line (CL-7, D-1158 Q3: why claim law doesn't apply), else null. */
  note: string | null
}

/** Tone by banner kind: a disabled station and hard states alert; booms read good. */
export function bannerTone(kind: string | null, stage: string | null): BannerTone {
  if (kind === 'boom') return 'good'
  if (stage === 'recovering') return 'warn'
  if (kind === 'disabled' || kind === 'war' || kind === 'blockade' || kind === 'lockdown' || kind === 'isolation') return 'alert'
  return 'warn'
}

/** A station view with its system's corporation claim block (null or absent when the corporation doesn't hold it). */
export type BannerStation = StationView & { corp?: ClaimCorpView | null }

/** The owner line from the station view's holder and tariff (and the corporation block for a corporation claim). */
export function ownerLine(v: Pick<StationView, 'holder' | 'tariff'> & { corp?: ClaimCorpView | null }): string {
  const h = v.holder
  if (h === null) return UNCLAIMED_LABELS.label
  const pct = Math.round(v.tariff * 100)
  if (h === PLAYER_FACTION_ID) {
    const corp = v.corp ?? null
    if (corp !== null) return fillLabel(CORP_CLAIM_LABELS.banner, { name: corpClaimName(corp), tariff: corpTariffText(corp.tariffSet, v.tariff) })
    return pct > 0 ? `Corporation claim · tariff ${pct}%` : 'Corporation claim'
  }
  if (FACTION_OUTLAW[FACTION[h]] === 1) return `${factionShort(h)} hold · lawless`
  return pct > 0 ? `${factionShort(h)} claim · tariff ${pct}%` : `${factionShort(h)} claim`
}

export function stateBannerModel(v: (Pick<StationView, 'banner' | 'holder' | 'tariff'> & { corp?: ClaimCorpView | null }) | null): StateBannerModel | null {
  if (v === null) return null
  const b = v.banner
  return { text: b?.text ?? null, tone: bannerTone(b?.kind ?? null, b?.stage ?? null), owner: ownerLine(v), note: v.holder === null ? UNCLAIMED_LABELS.note : null }
}

/** M2's docked station id → its strategic station view, with its system's corporation claim block (null = unknown). */
export type StateBannerSource = (stationId: string) => BannerStation | null

let source: StateBannerSource | null = null

/** Binds (or, with null, unbinds) where the banner reads its station views. */
export function setStateBannerSource(fn: StateBannerSource | null): void {
  source = fn
}

export function StateBannerView(p: { model: StateBannerModel }): ReactNode {
  const m = p.model
  return (
    <div className="ch-station" data-testid="charter-station">
      {m.text !== null ? (
        <p className="ui-banner ch-state-banner" data-tone={m.tone} role="status" data-testid="state-banner">
          {'⚠'} {m.text}
        </p>
      ) : null}
      <p className="ch-owner" data-testid="charter-owner">
        {m.owner}
      </p>
      {m.note !== null ? (
        <p className="ch-owner-note" data-testid="charter-owner-note">
          {m.note}
        </p>
      ) : null}
    </div>
  )
}

/** The banner and owner line of M2's docked station `stationId` (nothing when the source is unbound or unknown). */
export function StateBanner(p: { stationId: string }): ReactNode {
  const model = stateBannerModel(source?.(p.stationId) ?? null)
  return model === null ? null : <StateBannerView model={model} />
}
